import type { Config } from "../../../../aysa_api/models/config.types";
import type {
  Session,
  CustomerSession,
} from "../../../../aysa_api/models/session.types";
import type {
  MissionSuccessNotification,
  MissionIdentifier,
  MissionDetail,
} from "../../../../aysa_api/models/mission.types";
import type { ReductionsState } from "../../../../aysa_api/models/reductions_state.types";
import type { CoinsHistoryTransaction } from "../../../../aysa_api/models/coins_history_transaction.types";
import type { Image } from "../../../../aysa_api/models/image.types";
import type { RichText } from "../../../../aysa_api/models/rich_text.types";

export enum SubmitMissionProgressRequest_NewsletterMission_NewsletterStep {
  UNSPECIFIED = 0,
  SUBSCRIPTION = 1,
  CONFIRMATION = 2,
}

// gives us information if we should show the image in a frame or not
export enum ClaimMissionRewardResponse_RewardSummary_BadgeStyle {
  UNSPECIFIED = 0,
  STANDARD = 1,
  FRAMED = 2,
}

export interface GetActiveMissionsRequest {
  config: Config;
  session: Session;
}

export interface GetActiveMissionsResponse {
  missions: GetActiveMissionsResponse_ActiveMission[];
  missionBanners: GetActiveMissionsResponse_MissionBanner[];
}

export interface GetActiveMissionsResponse_ActiveMission {
  // e.g. 200
  reward: number;
  missionType?:
    | {
        $case: "pushNotificationMission";
        pushNotificationMission: GetActiveMissionsResponse_ActiveMission_PushNotificationMission;
      }
    | {
        $case: "addBirthdayMission";
        addBirthdayMission: GetActiveMissionsResponse_ActiveMission_AddBirthdayMission;
      }
    | {
        $case: "newsletterMission";
        newsletterMission: GetActiveMissionsResponse_ActiveMission_NewsletterMission;
      }
    | {
        $case: "sizeFinderMission";
        sizeFinderMission: GetActiveMissionsResponse_ActiveMission_SizeFinderMission;
      };
}

// mission types
export interface GetActiveMissionsResponse_ActiveMission_PushNotificationMission {}

export interface GetActiveMissionsResponse_ActiveMission_AddBirthdayMission {}

export interface GetActiveMissionsResponse_ActiveMission_NewsletterMission {}

export interface GetActiveMissionsResponse_ActiveMission_SizeFinderMission {}

export interface GetActiveMissionsResponse_MissionBanner {
  missionBanner?:
    | {
        $case: "settingsPageBanner";
        settingsPageBanner: GetActiveMissionsResponse_MissionBanner_SettingsPageBanner;
      }
    | {
        $case: "profileAndSecurityBanner";
        profileAndSecurityBanner: GetActiveMissionsResponse_MissionBanner_ProfileAndSecurityBanner;
      }
    | {
        $case: "newsletterPageBannerText";
        newsletterPageBannerText: GetActiveMissionsResponse_MissionBanner_NewsletterPageBannerText;
      }
    | {
        $case: "sizeFinderPageBanner";
        sizeFinderPageBanner: GetActiveMissionsResponse_MissionBanner_SizeFinderPageBanner;
      };
}

// mission banners
export interface GetActiveMissionsResponse_MissionBanner_SettingsPageBanner {
  // e.g: Get your order updates, deals & more
  title: string;
  // e.g. app: Join our newsletter, activate push-notifications and claim bonus coins.
  // e.g. web: Join our newsletter and claim bonus coins.
  description?: string;
}

export interface GetActiveMissionsResponse_MissionBanner_ProfileAndSecurityBanner {
  // e.g: Get your order updates, deals & more
  title: string;
  // e.g. app: Join our newsletter, activate push-notifications and claim bonus coins.
  // e.g. web: Join our newsletter and claim bonus coins.
  description?: string;
}

export interface GetActiveMissionsResponse_MissionBanner_NewsletterPageBannerText {
  // e.g: Get your order updates, deals & more
  title: string;
  // e.g. app: Join our newsletter, activate push-notifications and claim bonus coins.
  // e.g. web: Join our newsletter and claim bonus coins.
  description?: string;
}

export interface GetActiveMissionsResponse_MissionBanner_SizeFinderPageBanner {
  // e.g: Find your perfect fit
  title: string;
  // e.g. app: Add your sizes - get better recommendations and bonus coins.
  description?: string;
}

export interface SubmitMissionProgressRequest {
  config: Config;
  session: CustomerSession;
  mission?:
    | {
        $case: "pushNotificationMission";
        // synced missions (direct feedback)
        pushNotificationMission: SubmitMissionProgressRequest_PushNotificationMission;
      }
    | {
        $case: "addBirthdayMission";
        addBirthdayMission: SubmitMissionProgressRequest_AddBirthdayMission;
      }
    | {
        $case: "likeBrandMission";
        likeBrandMission: SubmitMissionProgressRequest_LikeBrandMission;
      }
    | {
        $case: "addToWishlistMission";
        addToWishlistMission: SubmitMissionProgressRequest_AddToWishlistMission;
      }
    | {
        $case: "sizeFinderMission";
        sizeFinderMission: SubmitMissionProgressRequest_SizeFinderMission;
      }
    | {
        $case: "newsletterMission";
        // async missions (indirect feedback)
        newsletterMission: SubmitMissionProgressRequest_NewsletterMission;
      };
}

export interface SubmitMissionProgressRequest_PushNotificationMission {}

export interface SubmitMissionProgressRequest_AddBirthdayMission {}

export interface SubmitMissionProgressRequest_LikeBrandMission {
  brandId: number;
}

export interface SubmitMissionProgressRequest_NewsletterMission {
  // default to mission v1 when step is not provided, and it is used for confirmation step.
  step?: SubmitMissionProgressRequest_NewsletterMission_NewsletterStep;
}

export interface SubmitMissionProgressRequest_AddToWishlistMission {
  productId: number;
}

export interface SubmitMissionProgressRequest_SizeFinderMission {}

export interface SubmitMissionProgressResponse {
  // completed mission details
  missionSuccessNotification?: MissionSuccessNotification;
}

export interface GetCoinsRequest {
  config: Config;
  session: CustomerSession;
  reductionsState: ReductionsState;
}

export interface GetCoinsResponse {
  availability?:
    | {
        $case: "available";
        available: GetCoinsResponse_Available;
      }
    | {
        $case: "unavailable";
        unavailable: GetCoinsResponse_Unavailable;
      };
}

export interface GetCoinsResponse_Available {
  // when coins become available or expire
  nextUpdate?: Date;
  // coins that are not yet available and cannot be redeemed
  pendingCoins: number;
  availableCoins: number;
}

// eg user is banned from coins
export interface GetCoinsResponse_Unavailable {}

export interface GetCoinsHistoryRequest {
  config: Config;
  session: CustomerSession;
  reductionsState: ReductionsState;
  // Once the continuation token is set, the element_count will be ignored since it is part of the continuation token.
  continuationToken?: string;
  // Will only be used in the first call to this EP without a continuation token.
  elementCount: number;
}

export interface Section {
  type?: {
    $case: "transaction";
    transaction: CoinsHistoryTransaction;
  };
}

export interface Header {
  availableCoins: Header_AvailableCoins;
  pendingCoins: Header_PendingCoins;
}

export interface Header_AvailableCoins {
  // eg 'Available Coins'
  title: string;
  coins: string;
}

export interface Header_PendingCoins {
  // eg 'Pending Coins'
  title: string;
  coins: string;
}

export interface GetCoinsHistoryResponse {
  continuationToken?: string;
  sections: Section[];
}

export interface GetCoinsHistoryHeaderRequest {
  config: Config;
  session: CustomerSession;
  reductionsState: ReductionsState;
}

export interface GetCoinsHistoryHeaderResponse {
  header: Header;
}

export interface GetCoinsHistoryTransactionByOrderIdRequest {
  config: Config;
  session: CustomerSession;
  reductionsState: ReductionsState;
  // order id to get the coins history transaction for
  orderId: string;
}

export interface GetCoinsHistoryTransactionByOrderIdResponse {
  // the coins history transaction for the order id
  transaction?: CoinsHistoryTransaction;
}

export interface GetMissionsHistoryRequest {
  config: Config;
  session: CustomerSession;
}

export interface GetMissionsHistoryResponse {
  items: GetMissionsHistoryResponse_Item[];
}

export interface GetMissionsHistoryResponse_Item {
  type?:
    | {
        $case: "content";
        content: GetMissionsHistoryResponse_Content;
      }
    | {
        $case: "mission";
        mission: MissionDetail;
      };
}

export interface GetMissionsHistoryResponse_Content {
  // E.g. You crushed every mission
  description: string;
}

export interface ClaimMissionRewardRequest {
  config: Config;
  session: CustomerSession;
  missionIdentifier: MissionIdentifier;
}

export interface ClaimMissionRewardResponse {
  result?:
    | {
        $case: "success";
        success: ClaimMissionRewardResponse_SuccessResult;
      }
    | {
        $case: "error";
        error: ClaimMissionRewardResponse_ErrorResult;
      };
}

export interface ClaimMissionRewardResponse_Benefit {
  // e.g. 2,000
  preClaimRedeemable: string;
  // e.g. 2,200
  postClaimRedeemable: string;
  // e.g. 200
  reward: string;
}

export interface ClaimMissionRewardResponse_RewardSummary {
  image: Image;
  // E.g. Mission Complete!
  headline: string;
  // E.g. You are now in the inner circle and have unlocked your reward.
  description: string;
  // E.g. Coins Balance:
  coinsBalance: string;
  // E.g. 50 coins expire in 60 days. Redeem them!
  expiring?: RichText;
  imageStyle: ClaimMissionRewardResponse_RewardSummary_BadgeStyle;
}

export interface ClaimMissionRewardResponse_SuccessResult {
  benefit?: ClaimMissionRewardResponse_Benefit;
  rewardSummary: ClaimMissionRewardResponse_RewardSummary;
}

export interface ClaimMissionRewardResponse_ErrorResult {
  // E.g. Oops, an error occurred!
  title: string;
  // E.g. We are unable currently to load the success message.
  description: string;
}
